import type { LocationListItem } from "@ca/db/locations";
import type { Pirep } from "@ca/faa/pireps";

import mapboxgl from "mapbox-gl";

import { formatDegrees } from "@ca/formatters/degrees";
import { formatDistance } from "@ca/formatters/distance";

export const MAPBOX_TOKEN = "pk.eyJ1Ijoic2hheW5lY2xhc3NhIiwiYSI6ImNtNzI2Nm85aTA2bWkya3BvcGZ3eW5xczQifQ.GrGOWhXXnLNDlBDGHGlUww";

const takeoffLandingSvg = (takeoff: boolean, rotorWing = false) => {
	const div = document.createElement("div");
    
	const color = takeoff ? "bg-primary-500/70 text-brand-500 border-brand-500" : "bg-brand-500/70 text-primary-500 border-primary-500";

	div.setAttribute("class", `${color}  border-2 rounded-full p-1 size-8 flex items-center justify-center`);
    
	const svgElement = document.createElementNS("http://www.w3.org/2000/svg", "svg");
	svgElement.setAttribute("viewBox", "0 0 24 24");
    
	// Fixed-wing paths
	const takeoffD = "M20.5 19h-17c-.55 0-1 .45-1 1s.45 1 1 1h17c.55 0 1-.45 1-1s-.45-1-1-1m1.57-9.36c-.22-.8-1.04-1.27-1.84-1.06L14.92 10L8.46 3.98a1.06 1.06 0 0 0-1.02-.25c-.68.19-1 .97-.65 1.58l3.44 5.96l-4.97 1.33l-1.57-1.24c-.25-.19-.57-.26-.88-.18l-.33.09c-.32.08-.47.45-.3.73l1.88 3.25c.23.39.69.58 1.12.47L21 11.48c.8-.22 1.28-1.04 1.07-1.84";
	const landingD = "M20.5 19h-17c-.55 0-1 .45-1 1s.45 1 1 1h17c.55 0 1-.45 1-1s-.45-1-1-1M3.51 11.61l15.83 4.24c.8.21 1.62-.26 1.84-1.06c.21-.8-.26-1.62-1.06-1.84l-5.31-1.42l-2.58-8.45a1.08 1.08 0 0 0-.75-.73c-.68-.18-1.35.33-1.35 1.04v6.88L5.15 8.95L4.4 7.09a.99.99 0 0 0-.67-.59l-.33-.09a.495.495 0 0 0-.63.48v3.75c0 .46.3.85.74.97";
	
	// Rotor-wing paths (helicopter with arrow indicators)
	const rotorWingTakeoffD = "M3 3h14v2H3zm20 3v4.5l-8.25 1.7A3.5 3.5 0 0 1 13 16.66V19h3v2H4a3 3 0 0 1-3-3v-1h2v1a1 1 0 0 0 1 1h1v-2.26c-1.75-.61-3-2.28-3-4.24C2 10 4 8 6.5 8H9V6h2v2h10V6zM11 19v-2H7v2zm-3.5-9c-1.38 0-2.5.9-2.5 2s1.12 2 2.5 2s2.5-.9 2.5-2s-1.12-2-2.5-2";
	const rotorWingLandingD = "M14.4 6H20v10h-7l-.4-2H7v7H5V4h9zm-.4 8h2v-2h2v-2h-2V8h-2v2l-1-2V6h-2v2H9V6H7v2h2v2H7v2h2v-2h2v2h2v-2l1 2zm-3-4V8h2v2zm3 0h2v2h-2z";

	let d: string;
	if (rotorWing) {
		d = takeoff ? rotorWingTakeoffD : rotorWingLandingD;
	} else {
		d = takeoff ? takeoffD : landingD;
	}

	const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
	path.setAttribute("fill", "currentColor");
	path.setAttribute("d", d);
	
	svgElement.appendChild(path);
	div.appendChild(svgElement);
	return div;
};

export const makeLandingMarker = ({ 
	position, 
	takeoff, 
	rotorWing = false, 
}: {
	position: mapboxgl.LngLatLike;
	takeoff: boolean;
	rotorWing?: boolean;
}) => {
	const marker = new mapboxgl.Marker(takeoffLandingSvg(takeoff, rotorWing));

	marker.setLngLat(position);

	return marker;
};

const pirepMarkerElement = (isUrgent = false) => {
	const div = document.createElement("div");
	const color = isUrgent ? "text-red-500" : "text-info-500";
	div.setAttribute("class", "rounded-full p-1 size-6 flex items-center justify-center cursor-pointer");
	
	const svgElement = document.createElementNS("http://www.w3.org/2000/svg", "svg");
	svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg");
	svgElement.setAttribute("width", "24");
	svgElement.setAttribute("height", "24");
	svgElement.setAttribute("viewBox", "0 0 24 24");
	svgElement.setAttribute("class", color);
	
	const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
	path.setAttribute("fill", "currentColor");
	path.setAttribute("d", "M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2m-3 12H7c-.55 0-1-.45-1-1s.45-1 1-1h10c.55 0 1 .45 1 1s-.45 1-1 1m0-3H7c-.55 0-1-.45-1-1s.45-1 1-1h10c.55 0 1 .45 1 1s-.45 1-1 1m0-3H7c-.55 0-1-.45-1-1s.45-1 1-1h10c.55 0 1 .45 1 1s-.45 1-1 1");
	
	svgElement.appendChild(path);
	div.appendChild(svgElement);
	return div;
};

export const makePirepMarker = ({ 
	position, 
	pirep,
}: {
	position: mapboxgl.LngLatLike,
	pirep: Pirep,
}) => {
	const marker = new mapboxgl.Marker(pirepMarkerElement(pirep.pirepType.toLowerCase().includes("urgent")));
	marker.setLngLat(position);

	// Create popup content for the PIREP
	const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
		<div class="flex flex-col p-1 max-w-sm">
			<h4 class="font-cta text-sm mb-1">${pirep.pirepType}</h4>
		
			<div class="text-xs mb-1 flex flex-col gap-1">
				<span><strong>Aircraft:</strong> ${pirep.acType}</span>
				<span><strong>Time:</strong> ${new Date(pirep.obsTime * 1000).toLocaleString()}</span>
				<span><strong>Flight Level:</strong> ${pirep.fltLvl}</span>
				<span><strong>Station:</strong> ${pirep.icaoId}</span>
			</div>
			<p class="text-xs bg-gray-100 p-1 rounded mt-1">${pirep.rawOb}</p>
		</div>
	`);

	marker.setPopup(popup);

	return marker;
};


export const computeHeading = (heading: null | number) => {
	const val = heading ?? 0;
	return val;
};

export const popupContent = (loc: LocationListItem, withLink = true) => {
	const link = withLink ? `<a 
		href="/aircraft/${loc.tailNumber}"
		class="font-cta text-lg hover:underline decoration-primary-500 decoration-2"
	>
	${loc.tailNumber}
	</a>` : `<p class='font-cta text-lg'>${loc.tailNumber}</p>`;

	const button = withLink ? `<button
		id="${loc.tailNumber}-popup-btn"
		class="bg-primary-500 text-brand-500 font-cta px-2 py-1 rounded-full mt-1"
	>
		Details
	</button>` : "";
	return `
		<div class="flex flex-col p-1">
			${link}
			<p>Speed: ${loc.speed} kts</p>
			<p>Altitude: ${formatDistance({ distance: loc.altitude, unit: "foot" })}</p>
			<p>Heading: ${formatDegrees(loc.heading, 0)}</p>
			<p>Updated: ${new Date(loc.timestamp).toLocaleString()}</p>
			${button}
		</div>
	`;
};

export const makerElement = (heading: null | number, flightState: "airborne" | "ground" | null, rotorWing = false) => {
	const div = document.createElement("div");
	const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
	svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
	svg.setAttribute("width", "32");
	svg.setAttribute("height", "32"); 
	svg.setAttribute("viewBox", "0 0 24 24");
	const color = flightState === "airborne" ? "bg-primary-500" : "bg-brand-200";
	svg.setAttribute("class", `aircraft-icon text-brand-500 p-1.5 rounded-full border-2 border-brand-500 ${color}`);
	svg.style.transformOrigin = "center";
	svg.style.transform = `rotate(${computeHeading(heading)}deg)`;

	const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
	path.setAttribute("fill", "currentColor");

	let d = "M22 14.6c0 .7-.67 1.2-1.34 1.01l-3.15-.93l-7.01-7.01V3.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V9l7.98 4.7c.32.18.52.53.52.9m-8.5-1.1L9.56 9.56L3.51 3.51A.996.996 0 1 0 2.1 4.92l5.67 5.67l-5.25 3.11c-.32.18-.52.53-.52.9c0 .7.67 1.2 1.34 1.01l7.16-2.1V19l-2.26 1.35c-.15.09-.24.26-.24.43v.58c0 .33.31.57.62.49l2.92-.73L12 21l.38.09l.42.11l1.9.48l.67.17c.32.08.62-.16.62-.49v-.58c0-.18-.09-.34-.24-.43L13.5 19v-2.67l5.57 5.57a.996.996 0 1 0 1.41-1.41z";
	if (flightState === "airborne") { 
		d = "M21.48 13.7L13.5 9V3.5c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5V9l-7.98 4.7c-.32.18-.52.53-.52.9c0 .7.67 1.2 1.34 1.01l7.16-2.1V19l-2.26 1.35c-.15.09-.24.26-.24.43v.58c0 .33.31.57.62.49l2.92-.73L12 21l.38.09l.42.11l1.9.48l.67.17c.32.08.62-.16.62-.49v-.58c0-.18-.09-.34-.24-.43L13.5 19v-5.5l7.16 2.1c.67.2 1.34-.3 1.34-1c0-.37-.2-.72-.52-.9";
	}
	if (rotorWing) {
		d = "M3 3h14v2H3zm20 3v4.5l-8.25 1.7A3.5 3.5 0 0 1 13 16.66V19h3v2H4a3 3 0 0 1-3-3v-1h2v1a1 1 0 0 0 1 1h1v-2.26c-1.75-.61-3-2.28-3-4.24C2 10 4 8 6.5 8H9V6h2v2h10V6zM11 19v-2H7v2zm-3.5-9c-1.38 0-2.5.9-2.5 2s1.12 2 2.5 2s2.5-.9 2.5-2s-1.12-2-2.5-2";
	}
	path.setAttribute("d", d);

	svg.appendChild(path);
	div.appendChild(svg);
	return div;
};

